import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  PublicDepartureBoardItem,
  PublicOperator,
  PublicSharedTrip,
  PublicTrip,
  PublicTripShare,
  PublicTripStop
} from '../../core/models/public-booking.model';
import { PublicBookingService } from '../../core/services/public-booking.service';

@Component({
  selector: 'app-public-departures',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './public-departures.component.html',
  styleUrls: ['./public-departures.component.css']
})
export class PublicDeparturesComponent implements OnInit, OnDestroy {
  @ViewChild('tripDrawer') tripDrawer?: ElementRef<HTMLElement>;
  @ViewChild('fallbackLink') fallbackLink?: ElementRef<HTMLInputElement>;

  departures: PublicDepartureBoardItem[] = [];
  operators: PublicOperator[] = [];
  selectedOperatorId: number | null = null;
  generatedAt: Date | null = null;
  currentTime = new Date();
  isLoading = true;
  errorMessage = '';
  currentPage = 0;
  rowsPerPage = 10;
  selectedTrip: PublicDepartureBoardItem | null = null;
  isSharedTripLive = true;
  isSharedTripLoading = false;
  sharedTripUnavailable = false;
  isSharing = false;
  shareError = '';
  fallbackShareUrl = '';
  notificationMessage = '';
  notificationIsError = false;

  private refreshTimer: number | null = null;
  private clockTimer: number | null = null;
  private pageTimer: number | null = null;
  private notificationTimer: number | null = null;
  private departuresRequest?: Subscription;
  private sharedTripRequest?: Subscription;
  private shareRequest?: Subscription;
  sharedTripFallback: PublicDepartureBoardItem | null = null;
  private activePublicToken: string | null = null;
  private readonly shareCache = new Map<number, PublicTripShare>();

  private hasLoadedDepartures = false;

  constructor(
    private publicBookingService: PublicBookingService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.updateRowsPerPage();
    this.loadOperators();
    this.route.paramMap.subscribe(params => {
      const token = params.get('publicToken');
      if (token === this.activePublicToken) return;
      this.activePublicToken = token;
      if (token) {
        this.loadSharedTrip(token);
      } else {
        this.sharedTripRequest?.unsubscribe();
        this.sharedTripFallback = null;
        this.sharedTripUnavailable = false;
        this.isSharedTripLoading = false;
      }
    });
    this.route.queryParamMap.subscribe(params => {
      const parsedOperatorId = Number(params.get('operatorId'));
      const operatorId = Number.isInteger(parsedOperatorId) && parsedOperatorId > 0 ? parsedOperatorId : null;
      const filterChanged = operatorId !== this.selectedOperatorId;
      this.selectedOperatorId = operatorId;
      if (filterChanged || !this.hasLoadedDepartures) {
        this.currentPage = 0;
        this.hasLoadedDepartures = true;
        this.loadDepartures();
      }
    });
    this.clockTimer = window.setInterval(() => this.currentTime = new Date(), 1000);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  ngOnDestroy(): void {
    this.departuresRequest?.unsubscribe();
    this.sharedTripRequest?.unsubscribe();
    this.shareRequest?.unsubscribe();
    this.clearTimers();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateRowsPerPage();
    this.currentPage = Math.min(this.currentPage, Math.max(0, this.pageCount - 1));
    this.startPageRotation();
  }

  onOperatorChange(): void {
    this.currentPage = 0;
    this.loadDepartures();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { operatorId: this.selectedOperatorId || null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  loadDepartures(): void {
    this.clearRefreshTimer();
    this.departuresRequest?.unsubscribe();
    this.departuresRequest = this.publicBookingService.getDepartures({
      operatorId: this.selectedOperatorId,
      limit: 20
    }).subscribe({
      next: response => {
        this.departures = Array.isArray(response.items) ? response.items : [];
        if (this.selectedTrip) {
          const liveTrip = this.departures.find(item => item.tripId === this.selectedTrip?.tripId);
          if (liveTrip) {
            this.selectedTrip = this.sharedTripFallback
              ? this.mergeSharedAndLiveTrip(this.sharedTripFallback, liveTrip)
              : liveTrip;
            this.isSharedTripLive = true;
          } else if (this.sharedTripFallback) {
            this.selectedTrip = this.sharedTripFallback;
            this.isSharedTripLive = false;
          } else {
            this.selectedTrip = null;
          }
        }
        this.generatedAt = response.generatedAtUtc ? new Date(response.generatedAtUtc) : new Date();
        this.errorMessage = '';
        this.isLoading = false;
        this.currentPage = Math.min(this.currentPage, Math.max(0, this.pageCount - 1));
        this.startPageRotation();
        this.scheduleRefresh(response.refreshAfterSeconds);
      },
      error: () => {
        this.errorMessage = this.departures.length
          ? 'Live updates are temporarily unavailable. Showing the last received departures.'
          : 'Departures are temporarily unavailable. We will try again shortly.';
        this.isLoading = false;
        this.scheduleRefresh(5);
      }
    });
  }

  get visibleDepartures(): PublicDepartureBoardItem[] {
    const start = this.currentPage * this.rowsPerPage;
    return this.departures.slice(start, start + this.rowsPerPage);
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.departures.length / this.rowsPerPage));
  }

  get pageIndexes(): number[] {
    return Array.from({ length: this.pageCount }, (_, index) => index);
  }

  hasEta(item: PublicDepartureBoardItem): boolean {
    return this.isTripInProgress(item)
      && item.nextStop?.estimatedMinutes !== undefined
      && item.nextStop?.estimatedMinutes !== null;
  }

  hasNextStop(item: PublicDepartureBoardItem): boolean {
    return this.isTripInProgress(item) && Boolean(item.nextStop?.name);
  }

  getEtaText(item: PublicDepartureBoardItem): string {
    if (!this.hasEta(item)) return '—';
    const minutes = Math.max(0, Math.round(Number(item.nextStop?.estimatedMinutes)));
    return this.formatDuration(minutes);
  }

  getStatusLabel(item: PublicDepartureBoardItem): string {
    return item.status.code === 'TRACKING_UNAVAILABLE'
      ? 'Status currently unavailable'
      : item.status.label;
  }

  getEtaTitle(item: PublicDepartureBoardItem): string {
    if (!this.isTripInProgress(item)) return 'Available while the trip is in progress';
    switch (item.nextStop?.etaConfidence) {
      case 'live': return 'Live GPS estimate';
      case 'scheduled': return 'Scheduled estimate';
      case 'stale': return 'Estimate based on stale tracking';
      case 'unavailable-vehicle-stationary': return 'ETA unavailable while vehicle is stationary';
      default: return 'Estimated time to next stop';
    }
  }

  selectTrip(item: PublicDepartureBoardItem): void {
    this.sharedTripFallback = null;
    this.isSharedTripLive = true;
    this.resetShareState();
    this.selectedTrip = item;
    if (this.pageTimer !== null) window.clearInterval(this.pageTimer);
    this.pageTimer = null;
    this.focusTripDrawer();
  }

  closeTripDetails(): void {
    this.selectedTrip = null;
    this.sharedTripFallback = null;
    this.resetShareState();
    this.startPageRotation();
    if (this.activePublicToken) {
      this.router.navigate(['/departures'], {
        queryParams: { operatorId: this.selectedOperatorId || null }
      });
    }
  }

  shareTrip(trip: PublicDepartureBoardItem): void {
    if (this.isSharing) return;
    this.isSharing = true;
    this.shareError = '';
    this.fallbackShareUrl = '';

    const cachedShare = this.shareCache.get(trip.tripId);
    if (cachedShare) {
      void this.presentShare(cachedShare, trip);
      return;
    }

    this.shareRequest?.unsubscribe();
    this.shareRequest = this.publicBookingService.shareTrip(trip.tripId).subscribe({
      next: share => {
        this.shareCache.set(trip.tripId, share);
        void this.presentShare(share, trip);
      },
      error: () => {
        this.isSharing = false;
        this.shareError = "We couldn't create the trip link. Please try again.";
        this.announce(this.shareError, true);
      }
    });
  }

  copyFallbackLink(): void {
    if (!this.fallbackShareUrl) return;
    void this.copyToClipboard(this.fallbackShareUrl);
  }

  getDayLabel(timestamp: string): string {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (this.isSameLocalDay(date, today)) return 'Today';
    if (this.isSameLocalDay(date, tomorrow)) return 'Tomorrow';
    return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(date);
  }

  getOperatorId(operator: PublicOperator): number | null {
    return Number(operator.operatorId ?? operator.operatorID ?? operator.organisationID ?? operator.organisationId) || null;
  }

  getOperatorName(operator: PublicOperator): string {
    return operator.operatorName || operator.orgName || operator.organisationName || operator.name || 'Operator';
  }

  getVehicleIdentification(item: PublicDepartureBoardItem): string {
    return item.vehicle.vehicleIdentificationNumber
      || item.vehicle.publicIdentifier
      || item.vehicle.label
      || '—';
  }

  useLogoFallback(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  trackByTripId(_: number, item: PublicDepartureBoardItem): number {
    return item.tripId;
  }

  trackByStop(index: number, stop: PublicTripStop): number | string {
    return stop.routeStopID ?? stop.stopOrder ?? stop.stopName ?? index;
  }

  private loadSharedTrip(publicToken: string): void {
    this.sharedTripRequest?.unsubscribe();
    this.isSharedTripLoading = true;
    this.sharedTripUnavailable = false;
    this.selectedTrip = null;
    this.sharedTripFallback = null;
    this.resetShareState();

    this.sharedTripRequest = this.publicBookingService.getSharedTrip(publicToken).subscribe({
      next: response => {
        const sharedTrip = this.mapSharedTrip(response);
        const liveTrip = this.departures.find(item => item.tripId === sharedTrip.tripId);
        this.sharedTripFallback = sharedTrip;
        this.selectedTrip = liveTrip ? this.mergeSharedAndLiveTrip(sharedTrip, liveTrip) : sharedTrip;
        this.isSharedTripLive = Boolean(liveTrip);
        this.isSharedTripLoading = false;
        if (this.pageTimer !== null) window.clearInterval(this.pageTimer);
        this.pageTimer = null;
        this.focusTripDrawer();
      },
      error: () => {
        this.isSharedTripLoading = false;
        this.sharedTripUnavailable = true;
      }
    });
  }

  private mapSharedTrip(response: PublicSharedTrip): PublicDepartureBoardItem {
    const trip = response.trip || {} as PublicTrip;
    const rawTrip = trip as PublicTrip & Record<string, unknown>;
    const operatorValue = rawTrip['operator'];
    const vehicleValue = rawTrip['vehicle'];
    const routeValue = rawTrip['route'];
    const operator = this.asRecord(operatorValue);
    const vehicle = this.asRecord(vehicleValue);
    const route = this.asRecord(routeValue);
    const stops = this.asArray<PublicTripStop>(
      trip.stops || trip.routeStops || route['stops'] || route['routeStops']
    );
    const firstStop = stops[0];
    const lastStop = stops[stops.length - 1];
    const tripId = Number(trip.tripID || trip.tripId || response.tripID);

    return {
      tripId,
      tripName: trip.tripName || this.stringValue(rawTrip, 'name'),
      routeName: trip.routeName || this.stringValue(route, 'name'),
      seatsAvailable: trip.seatsAvailable ?? trip.tripSeatsAvailable ?? this.numberValue(rawTrip, 'availableSeats'),
      stops,
      origin: {
        name: this.stopName(firstStop)
          || this.stringValue(this.asRecord(rawTrip['origin']), 'name')
          || 'Departure'
      },
      destination: {
        name: this.stopName(lastStop)
          || this.stringValue(this.asRecord(rawTrip['destination']), 'name')
          || 'Destination'
      },
      operator: {
        id: Number(trip.operatorID || trip.operatorId || this.numberValue(operator, 'id') || 0),
        name: trip.operatorName || trip.organisationName || this.stringValue(operator, 'name') || 'Operator',
        logoUrl: this.stringValue(operator, 'logoUrl') || null
      },
      vehicle: {
        label: trip.vehicleLabel || this.stringValue(vehicle, 'label'),
        registration: trip.vehicleRegistration || this.stringValue(vehicle, 'registration'),
        vehicleIdentificationNumber: trip.vehicleIdentificationNumber
          || this.stringValue(vehicle, 'vehicleIdentificationNumber'),
        publicIdentifier: this.stringValue(vehicle, 'publicIdentifier')
      },
      scheduledDepartureUtc: trip.tripStartTime || trip.departureTime
        || this.stringValue(rawTrip, 'scheduledDepartureUtc')
        || this.stringValue(rawTrip, 'departureUtc') || '',
      scheduledArrivalUtc: trip.tripEndTime || trip.arrivalTime
        || this.stringValue(rawTrip, 'scheduledArrivalUtc')
        || this.stringValue(rawTrip, 'arrivalUtc') || '',
      status: {
        code: 'SHARED_SCHEDULE',
        label: 'Schedule information',
        severity: 'info'
      }
    };
  }

  private mergeSharedAndLiveTrip(
    sharedTrip: PublicDepartureBoardItem,
    liveTrip: PublicDepartureBoardItem
  ): PublicDepartureBoardItem {
    return {
      ...sharedTrip,
      ...liveTrip,
      routeName: liveTrip.routeName || sharedTrip.routeName,
      seatsAvailable: liveTrip.seatsAvailable ?? sharedTrip.seatsAvailable,
      stops: liveTrip.stops?.length ? liveTrip.stops : sharedTrip.stops
    };
  }

  private async presentShare(share: PublicTripShare, trip: PublicDepartureBoardItem): Promise<void> {
    const shareData: ShareData = {
      title: trip.tripName || 'Trip details',
      text: this.getShareText(trip),
      url: share.shareUrl
    };

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (!this.isShareCancellation(error)) {
          this.showFallbackLink(share.shareUrl);
        }
      } finally {
        this.isSharing = false;
      }
      return;
    }

    await this.copyToClipboard(share.shareUrl);
    this.isSharing = false;
  }

  private async copyToClipboard(url: string): Promise<void> {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(url);
      this.fallbackShareUrl = '';
      this.announce('Trip link copied');
    } catch {
      this.showFallbackLink(url);
    }
  }

  private showFallbackLink(url: string): void {
    this.fallbackShareUrl = url;
    window.setTimeout(() => {
      this.fallbackLink?.nativeElement.focus();
      this.fallbackLink?.nativeElement.select();
    });
  }

  private getShareText(trip: PublicDepartureBoardItem): string {
    const details: string[] = [];
    if (trip.operator?.name) details.push(trip.operator.name);
    const departure = new Date(trip.scheduledDepartureUtc);
    if (!Number.isNaN(departure.getTime())) {
      details.push(`departs ${new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(departure)}`);
    }
    return details.length ? `Trip with ${details.join(', ')}` : 'View this trip';
  }

  private isShareCancellation(error: unknown): boolean {
    return Boolean(
      error
      && typeof error === 'object'
      && 'name' in error
      && (error as { name?: string }).name === 'AbortError'
    );
  }

  private announce(message: string, isError = false): void {
    if (this.notificationTimer !== null) window.clearTimeout(this.notificationTimer);
    this.notificationMessage = '';
    this.notificationIsError = isError;
    window.setTimeout(() => {
      this.notificationMessage = message;
      this.notificationTimer = window.setTimeout(() => {
        this.notificationMessage = '';
        this.notificationTimer = null;
      }, 4500);
    });
  }

  private resetShareState(): void {
    this.isSharing = false;
    this.shareError = '';
    this.fallbackShareUrl = '';
  }

  private focusTripDrawer(): void {
    window.setTimeout(() => this.tripDrawer?.nativeElement.focus());
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? value as Record<string, unknown> : {};
  }

  private asArray<T>(value: unknown): T[] {
    if (Array.isArray(value)) return value as T[];
    const record = this.asRecord(value);
    return Array.isArray(record['$values']) ? record['$values'] as T[] : [];
  }

  private stringValue(source: Record<string, unknown>, key: string): string {
    const value = source[key];
    return typeof value === 'string' ? value : '';
  }

  private numberValue(source: Record<string, unknown>, key: string): number | null {
    const value = Number(source[key]);
    return Number.isFinite(value) ? value : null;
  }

  private stopName(stop?: PublicTripStop): string {
    return stop?.stopName || stop?.name || '';
  }

  private loadOperators(): void {
    this.publicBookingService.getOperators().subscribe({
      next: operators => this.operators = operators,
      error: () => undefined
    });
  }

  private isSameLocalDay(left: Date, right: Date): boolean {
    return left.getFullYear() === right.getFullYear()
      && left.getMonth() === right.getMonth()
      && left.getDate() === right.getDate();
  }

  private isTripInProgress(item: PublicDepartureBoardItem): boolean {
    return Boolean(item.actualDepartureUtc)
      && !item.actualArrivalUtc
      && item.status.code !== 'COMPLETED';
  }

  private formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
  }

  private scheduleRefresh(seconds: number): void {
    if (document.hidden) return;
    const safeSeconds = Number.isFinite(Number(seconds)) ? Math.min(300, Math.max(2, Number(seconds))) : 5;
    this.refreshTimer = window.setTimeout(() => this.loadDepartures(), safeSeconds * 1000);
  }

  private startPageRotation(): void {
    if (this.pageTimer !== null) window.clearInterval(this.pageTimer);
    this.pageTimer = null;
    if (this.pageCount <= 1 || document.hidden || this.selectedTrip) return;
    this.pageTimer = window.setInterval(() => {
      this.currentPage = (this.currentPage + 1) % this.pageCount;
    }, 10000);
  }

  private updateRowsPerPage(): void {
    if (window.innerWidth <= 760) {
      this.rowsPerPage = 20;
      return;
    }
    const availableHeight = Math.max(300, window.innerHeight - 230);
    this.rowsPerPage = Math.max(5, Math.min(20, Math.floor(availableHeight / 68)));
  }

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.clearRefreshTimer();
      if (this.pageTimer !== null) window.clearInterval(this.pageTimer);
      this.pageTimer = null;
    } else {
      this.loadDepartures();
    }
  };

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) window.clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private clearTimers(): void {
    this.clearRefreshTimer();
    if (this.clockTimer !== null) window.clearInterval(this.clockTimer);
    if (this.pageTimer !== null) window.clearInterval(this.pageTimer);
    if (this.notificationTimer !== null) window.clearTimeout(this.notificationTimer);
  }
}
